'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import {
  type SendResetPasswordTokenSchema,
  sendResetPasswordTokenSchema,
} from '@labi-api-libs/schemas'
import { AlertCircle, CheckCircle2, User } from 'lucide-react'
import { useState } from 'react'

import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  useToast,
} from '@/components/ui'
import { useForm } from '@/hooks/useForm'
import { trackPasswordResetRequested } from '@/lib/analytics/events'

import { sendResetPasswordToken } from './actions/sendResetPasswordToken'

export default function ResetPassword() {
  const [showEmailSentMessage, setShowEmailSentMessage] = useState(false)

  const form = useForm<SendResetPasswordTokenSchema>({
    defaultValues: {
      email: '',
    },
    resolver: zodResolver(sendResetPasswordTokenSchema),
  })

  const { handleSubmit } = form

  const { toast } = useToast()

  const action: () => void = handleSubmit(async (data) => {
    const { message, success } = await sendResetPasswordToken(data)

    if (!success) {
      toast({
        title: 'Algo deu errado',
        description: message,
        variant: 'destructive',
        icon: AlertCircle,
      })
      return
    }

    trackPasswordResetRequested()
    setShowEmailSentMessage(true)
  })

  if (showEmailSentMessage) {
    return (
      <div className="flex w-full flex-col items-center gap-4 rounded-lg bg-system-background-base-contrast p-6">
        <CheckCircle2 className="text-system-state-success" size={40} />

        <div className="flex flex-col items-center gap-2 text-center">
          <span className="font-montserrat text-2xl font-bold text-system-text-primary">
            Link enviado!
          </span>
          <span className="font-medium text-system-text-secondary">
            Enviamos um link para o seu email. Com esse link, você poderá
            redefinir sua senha.
          </span>
        </div>
      </div>
    )
  }

  return (
    <Form {...form}>
      <form className="flex w-full flex-col items-center gap-4" action={action}>
        <FormField
          control={form.control}
          name="email"
          render={({ field, fieldState }) => (
            <FormItem className="w-full">
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  placeholder="Insira seu email"
                  variant={fieldState.error && 'error'}
                  icons={[
                    {
                      component: User,
                      position: 'start',
                    },
                  ]}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          className="w-full gap-2"
          variant="accent"
          disabled={form.buttonDisabled}
        >
          Enviar link para o email
        </Button>
      </form>
    </Form>
  )
}
